import { createRouter, createWebHistory } from '@ionic/vue-router';
import { RouteRecordRaw } from 'vue-router';
import UnauthenticatedTabs from "@/views/UnauthenticatedTabs.vue";
import AuthenticatedTabs from "@/views/AuthenticatedTabs.vue";

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    component: () => import('@/views/UnknownView.vue')
  },
  // Public RCS/SMS messaging compliance pages (served on oscodocs.com AND
  // autoquipdocs.com; brand auto-detected by hostname). Required for RCS agent
  // registration / vetting.
  {
    path: '/opt-in',
    component: () => import('@/views/messaging/OptInView.vue')
  },
  {
    path: '/opt-out',
    component: () => import('@/views/messaging/OptOutView.vue')
  },
  {
    path: '/privacy',
    component: () => import('@/views/messaging/PrivacyPolicyView.vue')
  },
  {
    path: '/terms',
    component: () => import('@/views/messaging/MessagingTermsView.vue')
  },
  {
    // 3D machine configurator (scissor, VRC and ram lift product lines).
    // Lazy route: three.js ships only in this chunk, never on other pages.
    path: '/configurator',
    component: () => import('@/views/ConfigPocView.vue')
  },
  {
    // JAB Becker compressor configurator (EN/DE). Same lazy-chunk rule:
    // three.js only inside this route's chunk.
    path: '/compressor-configurator',
    component: () => import('@/views/CompressorConfiguratorView.vue'),
    meta: { title: 'JAB Compressor Configurator' }
  },
  {
    path: '/ro/:id',
    component: () => import('@/views/ReadOnlyView.vue')
  },
  {
    // Bare machine simulation for embedding (OSCO Connect iframes this).
    // Lazy: three.js ships only in the simulation chunk.
    path: '/sim/:id',
    component: () => import('@/views/SimView.vue'),
    meta: { title: 'Machine Simulation' }
  },
  {
    // Unified customer-facing shopping cart. A cart may optionally
    // reference a machine; either way it renders here, not in ReadOnlyView.
    path: '/cart/:token',
    name: 'cart',
    component: () => import('@/views/CartView.vue')
  },
  {
    path: '/qs/:id',
    component: () => import('@/views/QuoteSurveyView.vue')
  },
  {
    path: '/loa/:id',
    component: () => import('@/views/LargeOrderAnnouncementView.vue')
  },
  {
    path: '/c/',
    component: UnauthenticatedTabs,
    meta: { title: 'Deliveries' },
    children: [
      {
        path: '',
        redirect: '/c/unknown'
      },
      {
        path: 'd/:id',
        component: () => import('@/views/DeliveryView.vue')
      },
      {
        path: 'di/:id',
        component: () => import('@/views/DeliveryItem.vue')
      },
      {
        path: 'dk/:id',
        component: () => import('@/views/DeliveryKit.vue')
      },
      {
        path: 'h/:id',
        component: () => import('@/views/HistoryView.vue')
      },
      {
        path: 'c/:id',
        component: () => import('@/views/CommentsView.vue')
      },
      {
        path: 'sv/:id',
        component: () => import('@/views/ServiceView.vue')
      },
      {
        path: 'unknown',
        component: () => import('@/views/UnknownView.vue')
      }
    ]
  },
  {
    path: '/a/',
    component: AuthenticatedTabs,
    children: [
      {
        path: '',
        redirect: '/c/unknown'
      },
      {
          path: 'sv/conversation/:id',
          name: 'sv/conversation',
          component: () => import('@/views/ServiceConversationView.vue')
      },
      {
        path: 'sv/new/conversation/:id',
        name: 'sv/new/conversation',
        component: () => import('@/views/CreateServiceConversationView.vue')
      },
      {
        path: 'cu/:id',
        name: 'cu',
        component: () => import('@/views/CustomerUploadView.vue')
      },
      {
        path: 'ai/',
        name: 'ai',
        component: () => import('@/views/AILabView.vue')
      }
    ]
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

// Per-feature browser tab title; index.html's <title>AutoDocs</title> is the default.
router.afterEach((to) => {
  const title = to.matched.map((record) => record.meta.title).filter(Boolean).pop() as string | undefined;
  document.title = title || 'AutoDocs';
});

export default router
